import { useEffect, useMemo, useRef, useState } from "react";
import { useSession } from "@/auth/session";
import {
  createInstallmentAgreement,
  listInstallmentsByContract,
  listPaymentMethods,
  updateInstallmentStatus,
  type InstallmentItem,
  type PaymentMethodItem,
  type PieVoucher,
} from "@/lib/api";
import VoucherPieModal from "./VoucherPieModal";

interface Props {
  contractId: string;
  contractNumber: string;
  deudaTotal?: number;
  deudaBoletas?: number;
  onClose: () => void;
  onChanged?: () => void;
}

type View = "list" | "new";

const STATUS_LABEL: Record<string, string> = {
  active: "Activo",
  completed: "Completado",
  cancelled: "Anulado",
  paused: "Pausado",
  repactada: "Repactado",
};

const STATUS_CLASS: Record<string, string> = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  completed: "bg-blue-50 text-blue-700 border-blue-200",
  cancelled: "bg-gray-100 text-gray-500 border-gray-200",
  paused: "bg-amber-50 text-amber-700 border-amber-200",
  repactada: "bg-purple-50 text-purple-700 border-purple-200",
};

const MODAL_TYPE = "repactacion";

function currentPeriod(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

export default function RepactacionModal({ contractId, contractNumber, deudaTotal = 0, deudaBoletas = 0, onClose, onChanged }: Props) {
  const { session } = useSession();
  const [view, setView] = useState<View>("list");
  const [items, setItems] = useState<InstallmentItem[]>([]);
  const [convenios, setConvenios] = useState<InstallmentItem[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [voucher, setVoucher] = useState<PieVoucher | null>(null);
  const submittingRef = useRef(false);

  const [form, setForm] = useState({
    totalAmount: deudaTotal > 0 ? String(deudaTotal) : "",
    pie: "",
    piePaymentMethodCode: "",
    installments: "6",
    startPeriod: currentPeriod(),
    endPeriod: "",
    parentAgreementId: "",
    notes: "",
  });

  const debtAfterPie = useMemo(() => {
    const total = Number(form.totalAmount.replace(/\./g, "").replace(",", "."));
    const pie = Number(form.pie.replace(/\./g, "").replace(",", "."));
    return Math.max(total - pie, 0);
  }, [form.totalAmount, form.pie]);

  const installmentAmount = useMemo(() => {
    const cuotas = parseInt(form.installments, 10);
    if (!debtAfterPie || !cuotas || cuotas <= 0) return 0;
    return Math.round(debtAfterPie / cuotas);
  }, [debtAfterPie, form.installments]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!session) return;
    void load();
  }, [session?.userId]);

  // Auto-rellenar deuda total al seleccionar convenio padre
  useEffect(() => {
    if (!form.parentAgreementId) {
      setForm((prev) => ({ ...prev, totalAmount: deudaTotal > 0 ? String(deudaTotal) : "" }));
      return;
    }
    const convenio = convenios.find((c) => c.id === form.parentAgreementId);
    if (!convenio) return;
    const saldoConvenio = Math.max(
      Number(convenio.totalAmount) - Number(convenio.paidInstallments) * Number(convenio.installmentAmount),
      0
    );
    const total = deudaBoletas + saldoConvenio;
    setForm((prev) => ({ ...prev, totalAmount: total > 0 ? String(total) : "" }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.parentAgreementId]);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const [reps, convs, methods] = await Promise.all([
        listInstallmentsByContract(session!, contractId, MODAL_TYPE),
        listInstallmentsByContract(session!, contractId, "convenio"),
        listPaymentMethods(session!),
      ]);
      setItems(reps);
      setConvenios(convs.filter((c) => c.status === "active"));
      const manualMethods = methods.filter((method) => method.manualEnabled && method.status === "active");
      setPaymentMethods(manualMethods);
      setForm((previous) => ({
        ...previous,
        piePaymentMethodCode: previous.piePaymentMethodCode || manualMethods[0]?.code || "",
      }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al cargar repactaciones");
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    if (!session || submittingRef.current) return;
    const total = Number(form.totalAmount.replace(/\./g, "").replace(",", "."));
    const pie = Number(form.pie.replace(/\./g, "").replace(",", "."));
    const cuotas = parseInt(form.installments, 10);
    if (!total || total <= 0) { setError("Ingresa un monto válido"); return; }
    if (pie < 0 || pie >= total) { setError("El pie debe ser menor al monto total"); return; }
    if (pie > 0 && !form.piePaymentMethodCode) { setError("Selecciona la forma de pago del pie"); return; }
    if (!cuotas || cuotas <= 0) { setError("Ingresa un número de cuotas válido"); return; }
    if (!form.startPeriod.match(/^\d{4}-\d{2}$/)) { setError("Período de inicio inválido (formato YYYY-MM)"); return; }

    submittingRef.current = true;
    setSaving(true);
    setError("");
    try {
      const result = await createInstallmentAgreement(session, {
        contractId,
        type: MODAL_TYPE,
        pie: pie > 0 ? pie : undefined,
        piePaymentMethodCode: pie > 0 ? form.piePaymentMethodCode : undefined,
        parentAgreementId: form.parentAgreementId || undefined,
        totalAmount: total,
        installments: cuotas,
        startPeriod: form.startPeriod,
        endPeriod: form.endPeriod || undefined,
        notes: form.notes || undefined,
      });
      await load();
      onChanged?.();
      if (result.voucherData) {
        setVoucher(result.voucherData);
      }
      setForm({ totalAmount: "", pie: "", piePaymentMethodCode: paymentMethods[0]?.code || "", installments: "6", startPeriod: currentPeriod(), endPeriod: "", parentAgreementId: "", notes: "" });
      setView("list");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al crear repactación");
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  }

  async function handleCancel(id: string) {
    if (!session || !confirm("¿Anular esta repactación?")) return;
    setCancellingId(id);
    try {
      await updateInstallmentStatus(session, id, "cancelled");
      await load();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al anular");
    } finally {
      setCancellingId(null);
    }
  }

  async function handleComplete(id: string) {
    if (!session || !confirm("¿Marcar esta repactación como completada?")) return;
    setCancellingId(id);
    try {
      await updateInstallmentStatus(session, id, "completed");
      await load();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al actualizar");
    } finally {
      setCancellingId(null);
    }
  }

  const set = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/40" onClick={onClose} />
        <div className="relative bg-white rounded-2xl w-full max-w-lg shadow-2xl flex flex-col max-h-[90vh]">

          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <div>
              <h2 className="text-base font-semibold text-gray-800">
                {view === "list" ? "Repactaciones" : "Nueva repactación"}
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">Contrato {contractNumber}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
              <i className="ri-close-line text-xl" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            {error ? (
              <div className="mb-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>
            ) : null}

            {/* LISTA */}
            {view === "list" && (
              <div className="space-y-3">
                {loading ? (
                  <div className="space-y-2">
                    {[1, 2].map((i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-gray-100" />)}
                  </div>
                ) : items.length === 0 ? (
                  <div className="py-10 text-center">
                    <i className="ri-refresh-line text-3xl text-gray-300" />
                    <p className="mt-2 text-sm text-gray-500">Sin repactaciones para este contrato.</p>
                  </div>
                ) : (
                  items.map((item) => (
                    <div key={item.id} className={`rounded-xl border p-4 ${item.status === "cancelled" ? "opacity-60" : ""}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-gray-800">${Number(item.totalAmount).toLocaleString("es-CL")}</span>
                            <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${STATUS_CLASS[item.status] ?? STATUS_CLASS.active}`}>
                              {STATUS_LABEL[item.status] ?? item.status}
                            </span>
                          </div>
                          {item.pie > 0 && (
                            <p className="mt-1 text-xs text-amber-700 font-medium">
                              Pie: ${Number(item.pie).toLocaleString("es-CL")} (pagado en caja)
                            </p>
                          )}
                          <p className="mt-1 text-xs text-gray-500">
                            {item.installments} cuotas de ${Number(item.installmentAmount).toLocaleString("es-CL")}
                            {" · "}desde {item.startPeriod}
                            {item.endPeriod ? ` hasta ${item.endPeriod}` : ""}
                          </p>
                          <p className="mt-0.5 text-xs text-gray-400">
                            Cuotas pagadas: {item.paidInstallments}/{item.installments}
                          </p>
                          {item.notes ? <p className="mt-1 text-xs text-gray-400 italic">{item.notes}</p> : null}
                          <p className="mt-1 text-[10px] text-gray-300">
                            Creado {new Date(item.createdAt).toLocaleDateString("es-CL")}
                          </p>
                        </div>
                        {item.status === "active" ? (
                          <div className="flex shrink-0 flex-col gap-1">
                            <button
                              onClick={() => handleComplete(item.id)}
                              disabled={cancellingId === item.id}
                              className="text-xs px-2 py-1 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                            >
                              Completar
                            </button>
                            <button
                              onClick={() => handleCancel(item.id)}
                              disabled={cancellingId === item.id}
                              className="text-xs px-2 py-1 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50"
                            >
                              {cancellingId === item.id ? "..." : "Anular"}
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* FORMULARIO */}
            {view === "new" && (
              <div className="space-y-4">
                <div className="rounded-lg bg-amber-50 border border-amber-100 px-4 py-3 text-xs text-amber-700">
                  Una repactación reestructura la deuda de un convenio o saldo previo. El pie se cobra <strong>en caja al momento de firmar</strong> y genera un voucher de respaldo.
                </div>

                {convenios.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Convenio anterior que reemplaza (opcional)</label>
                    <select
                      value={form.parentAgreementId}
                      onChange={set("parentAgreementId")}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none bg-white"
                    >
                      <option value="">Sin convenio previo asociado</option>
                      {convenios.map((c) => (
                        <option key={c.id} value={c.id}>
                          ${Number(c.totalAmount).toLocaleString("es-CL")} · {c.installments} cuotas · desde {c.startPeriod}
                        </option>
                      ))}
                    </select>
                    {form.parentAgreementId && (
                      <p className="mt-1 text-xs text-amber-600">El convenio seleccionado quedará marcado como "repactado".</p>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Deuda total a repactar ($)</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={form.totalAmount}
                    onChange={set("totalAmount")}
                    placeholder="Ej: 120000"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
                    required
                  />
                  {/* Desglose de deuda */}
                  {deudaTotal > 0 && (
                    <div className="mt-2 rounded-lg bg-amber-50 border border-amber-100 px-3 py-2 space-y-1">
                      {deudaBoletas > 0 && (
                        <div className="flex justify-between text-xs text-amber-700">
                          <span>Boletas pendientes</span>
                          <span>${deudaBoletas.toLocaleString("es-CL")}</span>
                        </div>
                      )}
                      {form.parentAgreementId && (() => {
                        const conv = convenios.find((c) => c.id === form.parentAgreementId);
                        if (!conv) return null;
                        const saldo = Math.max(Number(conv.totalAmount) - Number(conv.paidInstallments) * Number(conv.installmentAmount), 0);
                        return (
                          <div className="flex justify-between text-xs text-amber-700">
                            <span>Saldo convenio ({conv.paidInstallments}/{conv.installments} cuotas pagadas)</span>
                            <span>${saldo.toLocaleString("es-CL")}</span>
                          </div>
                        );
                      })()}
                      <div className="flex justify-between text-xs font-semibold text-amber-800 border-t border-amber-200 pt-1 mt-1">
                        <span>Total a repactar</span>
                        <span>${deudaTotal.toLocaleString("es-CL")}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Pie (pago inmediato en caja) ($)</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={form.pie}
                    onChange={set("pie")}
                    placeholder="Ej: 20000"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
                  />
                  <p className="mt-1 text-xs text-gray-400">Saldo a cuotar: <strong className="text-gray-700">${debtAfterPie.toLocaleString("es-CL")}</strong></p>
                </div>

                {Number(form.pie.replace(/\./g, "").replace(",", ".")) > 0 && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Forma de pago del pie</label>
                    <select
                      value={form.piePaymentMethodCode}
                      onChange={set("piePaymentMethodCode")}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
                      required
                    >
                      {paymentMethods.map((method) => (
                        <option key={method.id} value={method.code}>{method.name}</option>
                      ))}
                    </select>
                    {paymentMethods.length === 0 && (
                      <p className="mt-1 text-xs text-red-500">No hay formas de pago manual activas.</p>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">N° de cuotas</label>
                    <input
                      type="number"
                      min={1}
                      max={60}
                      value={form.installments}
                      onChange={set("installments")}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Valor cuota</label>
                    <div className="flex items-center rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-700">
                      ${installmentAmount > 0 ? installmentAmount.toLocaleString("es-CL") : "—"}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Período inicio (YYYY-MM)</label>
                    <input
                      type="text"
                      value={form.startPeriod}
                      onChange={set("startPeriod")}
                      placeholder="2026-07"
                      pattern="\d{4}-\d{2}"
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Período fin (opcional)</label>
                    <input
                      type="text"
                      value={form.endPeriod}
                      onChange={set("endPeriod")}
                      placeholder="2026-12"
                      pattern="\d{4}-\d{2}"
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Observaciones (opcional)</label>
                  <textarea
                    value={form.notes}
                    onChange={set("notes")}
                    rows={2}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none resize-none"
                    placeholder="Ej: Repactación de convenio anterior vencido. El cliente compromete pago el día 5 de cada mes."
                  />
                </div>

                {installmentAmount > 0 && (
                  <div className="rounded-lg bg-amber-50 border border-amber-100 px-4 py-3 space-y-1">
                    {Number(form.pie) > 0 && (
                      <p className="text-xs text-amber-700">
                        <strong>Pie:</strong> ${Number(form.pie.replace(/\./g, "")).toLocaleString("es-CL")} (cobrar en caja ahora)
                      </p>
                    )}
                    <p className="text-xs text-amber-700">
                      <strong>{form.installments} cuotas</strong> de <strong>${installmentAmount.toLocaleString("es-CL")}</strong> desde <strong>{form.startPeriod}</strong>
                      {form.endPeriod ? ` hasta ${form.endPeriod}` : ""}
                    </p>
                    <p className="text-xs text-amber-600">Las cuotas se cobrarán automáticamente en la boleta mensual.</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-6 py-4">
            {view === "list" ? (
              <>
                <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700">Cerrar</button>
                <button
                  onClick={() => { setError(""); setView("new"); }}
                  className="flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium text-white hover:bg-amber-600"
                >
                  <i className="ri-add-line" /> Nueva repactación
                </button>
              </>
            ) : (
              <>
                <button onClick={() => { setError(""); setView("list"); }} className="text-sm text-gray-500 hover:text-gray-700">
                  ← Volver
                </button>
                <button
                  type="button"
                  disabled={saving || submittingRef.current}
                  onClick={() => void handleCreate()}
                  className="flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium text-white hover:bg-amber-600 disabled:opacity-60"
                >
                  {saving
                    ? <><i className="ri-loader-4-line animate-spin" /> Guardando...</>
                    : <><i className="ri-save-line" /> Crear y cobrar pie</>}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {voucher && <VoucherPieModal voucher={voucher} onClose={() => setVoucher(null)} />}
    </>
  );
}
